import { css } from '@emotion/react'
import { Alert, colors, HFlow, Icon, InfoLabel, TableFooter, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { DateTime } from 'components/date'
import { Telefone } from 'components/label'
import { HideOnScreenSize } from 'components/responsive'
import { ButtonLink } from 'components/route'
import { TableBox, usePagedTableProps } from 'components/table'
import theme from 'config/theme'
import { useGarantiaAcessoTableLazyQuery } from 'graphql/hooks.generated'
import { TipoEstabelecimentoEnum } from 'graphql/types.generated'
import { usePubSub } from 'hooks/pubsub/usePubSub'
import { Fragment, useEffect } from 'react'
import { useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions.generated'
import { humanizeAge } from 'util/date/humanize-age'
import { upperFirst } from 'util/lang'
import StatusSquare from 'view/atendimentos/detail/historico/components/StatusSquare'
import { ClassificacaoPrioridadeCuidadoRecord } from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'

import { tipoAtendimentoRecord } from './components/TipoAtendimentoSelectField'
import { convertGarantiaAcessoFilterModelToInput } from './convert-garantiaAcesso'
import { GarantiaAcessoTableDropdownMenu } from './GarantiaAcessoTableDropdownMenu'
import { GarantiaAcessoTablePanel } from './GarantiaAcessoTablePanel'
import { GarantiaAcessoFilterModel, GarantiaAcessoItem } from './model-garantiaAcesso'

const PLACEHOLDER = '—'

type GarantiaAcessoTopicModel = { idUbs: number }

interface GarantiaAcessoTableProps {
  filter: GarantiaAcessoFilterModel
  onChangeFilter: (filter: GarantiaAcessoFilterModel) => void
}

export default function GarantiaAcessoTable(props: GarantiaAcessoTableProps) {
  const { filter, onChangeFilter } = props
  const match = useRouteMatch()
  const {
    acesso: { unidadeSaude },
    tipoEstabelecimento,
  } = useAcessoLotacaoOrEstagio()
  const [executeQuery, { data, loading, refetch }] = useGarantiaAcessoTableLazyQuery({
    fetchPolicy: 'cache-and-network',
  })
  const isCEO = tipoEstabelecimento === TipoEstabelecimentoEnum.CEO

  useEffect(() => {
    executeQuery({ variables: { input: convertGarantiaAcessoFilterModelToInput(filter) } })
  }, [executeQuery, filter])

  const garantiaAcesso = data?.garantiaAcessoByUnidadeSaudeId

  usePubSub<GarantiaAcessoTopicModel>({
    topic: `/garantiaacesso/${unidadeSaude.id}`,
    onMessage: (data) => data?.idUbs && refetch(),
  })

  const tableProps = usePagedTableProps({
    loading,
    result: garantiaAcesso,
    onChange: onChangeFilter,
  })

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    event.stopPropagation()
    if (event.code === 'Space') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }

  const renderActions = (item: GarantiaAcessoItem) => (
    <HFlow hSpacing={0.5} justifyContent='flex-end'>
      {(!item.cuidadoCompartilhadoEvolucao || item.cidadao.faleceu) && (
        <Tooltip text='Remover da lista'>
          <ButtonLink
            size='small'
            skin='ghost'
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            to={`${match.url}/remover-cidadao/${item.id}`}
            data-testid='ButtonLink.RemoverLista'
          >
            <Icon icon='userTimes' />
          </ButtonLink>
        </Tooltip>
      )}
      {!item.cidadao.faleceu && (
        <CheckPermission permission={Permissions.visualizarAgenda.agendarEEditar}>
          <Tooltip text='Agendar consulta'>
            <ButtonLink
              size='small'
              skin='ghost'
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              to={`${match.url}/agendar-consulta/${item.id}`}
              data-testid='ButtonLink.AgendarConsulta'
            >
              <Icon icon='calendarOutline' />
            </ButtonLink>
          </Tooltip>
        </CheckPermission>
      )}
      <GarantiaAcessoTableDropdownMenu
        cidadao={item.cidadao}
        garantiaAcessoId={item.id}
        cuidadoCompartilhadoId={item.cuidadoCompartilhadoEvolucao?.cuidadoCompartilhado.id}
        dataTestId='ButtonLink.MaisOpcoes'
      />
    </HFlow>
  )
  return (
    <VFlow>
      {!isCEO && (
        <Alert type='info' inline>
          Não é possível editar ou remover registros originados do Cuidado compartilhado. A remoção só será possível com
          o agendamento ou em caso de óbito do cidadão.
        </Alert>
      )}
      <TableBox>
        <AccordionDataTable<GarantiaAcessoItem>
          loading={loading}
          dataTestId='AccordionDataTable.GarantiaAcesso'
          columns={[
            {
              name: 'dataEntrada',
              header: 'Data',
              render: (item) => <DateTime format='DD/MM/YYYY' value={item.dataEntrada} />,
            },
            {
              name: 'origem',
              header: 'Origem',
              render: renderOrigem,
              hide: isCEO,
              style: [styles.dataColumn],
            },
            {
              name: 'cidadao',
              header: 'Cidadão',
              render: (item) => renderCidadao(item),
            },
            {
              name: 'equipe',
              header: 'Equipe',
              render: renderEquipe,
              hide: isCEO,
            },
            {
              name: 'tipoAtendimento',
              header: 'Tipo de atendimento',
              render: renderTipoAtendimento,
              hide: isCEO,
            },
            {
              name: 'tipoServico',
              header: 'Tipo de serviço',
              render: (item) => <Text>{upperFirst(item.tipoServico?.nome.toLowerCase()) || PLACEHOLDER}</Text>,
              hide: !isCEO,
            },
            {
              name: 'actions',
              render: renderActions,
              style: styles.actionsColumn,
            },
          ]}
          components={{ AccordionPanel: GarantiaAcessoTablePanel }}
          {...tableProps}
        />
        <TableFooter {...tableProps} pageSize={filter.pageParams?.size} sizeOptions={[5, 15, 25, 50]} />
      </TableBox>
    </VFlow>
  )
}

const renderCidadao = (item: GarantiaAcessoItem) => {
  const { telefoneContato, telefoneCelular, telefoneResidencial, faleceu } = item.cidadao

  return (
    <VFlow vSpacing={faleceu ? 1 : 0} style={styles.columnHeight}>
      <Text fontWeight='bold'>{(item.cidadao.nomeSocial ?? item.cidadao.nome).titleCase()}</Text>
      {faleceu ? (
        <Tag>Óbito</Tag>
      ) : (
        <VFlow vSpacing={0}>
          <Text>{humanizeAge(item.cidadao.dataNascimento)}</Text>
          <Telefone value={telefoneContato || telefoneCelular || telefoneResidencial} />
        </VFlow>
      )}
    </VFlow>
  )
}

const renderOrigem = (item: GarantiaAcessoItem) =>
  item.cuidadoCompartilhadoEvolucao ? (
    <Tag
      type='normal'
      style={css`
        background: ${colors.pink.c90};
        color: ${colors.pink.c40};
      `}
    >
      Cuidado compartilhado
    </Tag>
  ) : (
    <Tag
      type='normal'
      style={css`
        background: ${colors.orange.c90};
        color: ${colors.orange.c40};
      `}
    >
      Garantia do acesso
    </Tag>
  )

const renderTipoAtendimento = (item: GarantiaAcessoItem) => {
  const prioridade =
    ClassificacaoPrioridadeCuidadoRecord[
      item.cuidadoCompartilhadoEvolucao?.cuidadoCompartilhado.classificacaoPrioridadeAtual
    ]
  return (
    <Fragment>
      <HideOnScreenSize up='lg'>
        <VFlow vSpacing={0}>
          <Text>{tipoAtendimentoRecord[item.tipoAtendimento]?.nome || PLACEHOLDER}</Text>
          {prioridade && (
            <Tooltip text={prioridade.descricaoEstendida}>
              {prioridade && <StatusSquare color={prioridade.cor} description={prioridade.descricao} />}
            </Tooltip>
          )}
        </VFlow>
      </HideOnScreenSize>
      <HideOnScreenSize down='lg'>
        <VFlow vSpacing={0}>
          <Text>{tipoAtendimentoRecord[item.tipoAtendimento]?.nome || PLACEHOLDER}</Text>
          {prioridade && (
            <StatusSquare
              color={prioridade.cor}
              description={prioridade.descricaoEstendida}
              style={css`
                inline-size: max-content;
              `}
            />
          )}
        </VFlow>
      </HideOnScreenSize>
    </Fragment>
  )
}

const renderEquipe = (item: GarantiaAcessoItem) => {
  const equipeLotacaoExecutante = item.cuidadoCompartilhadoEvolucao?.lotacaoExecutante.equipe
  return item.equipe ? (
    <InfoLabel title={item.equipe.nome} placeholder={PLACEHOLDER}>
      {item.equipe.area && <Text>{'Área ' + item.equipe.area}</Text>}
    </InfoLabel>
  ) : equipeLotacaoExecutante ? (
    <InfoLabel title={equipeLotacaoExecutante.nome} placeholder={PLACEHOLDER}>
      {equipeLotacaoExecutante.area && <Text>{'Área ' + equipeLotacaoExecutante.area}</Text>}
    </InfoLabel>
  ) : (
    <Text>Sem equipe</Text>
  )
}

const styles = {
  dataColumn: css`
    ${theme.breakpoints.down('lg')} {
      flex: 2;
    }
    min-width: 11rem;
    max-width: 11rem;
  `,
  actionsColumn: css`
    min-width: fit-content;
  `,
  columnHeight: css`
    min-height: 4rem;
    justify-content: space-evenly;
  `,
}
